import _ from 'lodash'

import { Granularity } from '@/types/variable'
import { Interval, ParameterizationType } from '@/types/variable'
import {
  haveSameStoredTransitions,
  storedTransitionsByBoundary,
  StoredTransitionAtBoundary,
  TransitionBoundarySource,
} from '@/utils/network/transitionFunctions'

import { VariableToCompare } from './pairEntitiesInTwoNetwork'

export type ChangedVariableFieldsInput = Pick<
  VariableToCompare,
  | 'name'
  | 'intervalUnit'
  | 'intervals'
  | 'stateNames'
  | 'transitionFunctions'
  | 'optimalStateName'
  | 'granularity'
  | 'comment'
  | 'parameterizationType'
  | 'tags'
  | 'definition'
  | 'references'
  | 'formula'
  | 'prevalenceExplanation'
> & {
  type: { name: string }
  inboundArrows: Array<{ name: string; comment: string }>
}

/**
 * Discriminated union of variable-field changes detected between a
 * paired (old, new) variable. Both `compareNetworks` (text release-
 * note download) and `buildNetworkComparison` (assistant structured
 * tool) iterate this list to drive their emission. Adding a new
 * variable field that should participate in cross-network comparison
 * is a single edit here — both consumers pick up the new event kind
 * automatically (plus a small switch case to render it).
 *
 * Probabilities (CPT) are intentionally NOT in this list. The text
 * tool guards them behind a separate `shownChanges.probability` flag
 * (default off) and the structured assistant tool deliberately does
 * not surface them, so running the relatively expensive
 * `isCPTChanged` check unconditionally just to centralize it would
 * cost more than the existing call-site guard.
 *
 * `null` / `undefined` long-text fields are normalized to '' on the
 * way out so consumers can rely on `from` / `to` being plain strings
 * for the formatted-text channels (comment, definition, references,
 * formula, prevalenceExplanation). Arrow comments preserve the raw
 * `string | undefined` because the text release-note distinguishes
 * "missing arrow" from "empty comment".
 */
export type ChangedVariableField =
  | { kind: 'name'; from: string; to: string }
  | { kind: 'type'; from: string; to: string }
  | { kind: 'intervalUnit'; from: string; to: string }
  | {
      kind: 'intervals'
      from: Interval[] | undefined
      to: Interval[] | undefined
      fromStates: string[]
      toStates: string[]
    }
  | {
      kind: 'transitionFunctions'
      from: StoredTransitionAtBoundary[]
      to: StoredTransitionAtBoundary[]
    }
  | {
      kind: 'parameterizationType'
      from: ParameterizationType
      to: ParameterizationType
    }
  | { kind: 'states'; from: string[]; to: string[] }
  | { kind: 'optimalState'; from: string; to: string }
  | { kind: 'granularity'; from: Granularity; to: Granularity }
  | { kind: 'comment'; from: string; to: string }
  | { kind: 'tags'; from: string[]; to: string[] }
  | { kind: 'definition'; from: string; to: string }
  | { kind: 'references'; from: string; to: string }
  | { kind: 'formula'; from: string; to: string }
  | { kind: 'prevalenceExplanation'; from: string; to: string }
  | {
      kind: 'arrowComment'
      inboundVariableName: string
      from: string | undefined
      to: string | undefined
    }

export type ChangedVariableFieldKind = ChangedVariableField['kind']

/**
 * Produce the ordered list of field-level changes between two paired
 * variables. The order matches the original sequence the text-release-
 * note tool emitted in (name → type → intervalUnit → intervals →
 * transitionFunctions → parameterizationType → states → optimalState → granularity → comment
 * → tags → definition → references → formula → prevalenceExplanation
 * → arrowComment) so downstream rendering stays stable when callers
 * iterate without sorting.
 */
function transitionSource(
  variable: ChangedVariableFieldsInput,
): TransitionBoundarySource {
  return {
    intervals: variable.intervals,
    stateRefs: variable.stateNames,
    transitionFunctions: variable.transitionFunctions,
  }
}

export function getChangedVariableFields(
  oldVariable: ChangedVariableFieldsInput,
  newVariable: ChangedVariableFieldsInput,
): ChangedVariableField[] {
  const events: ChangedVariableField[] = []

  if (oldVariable.name !== newVariable.name) {
    events.push({
      kind: 'name',
      from: oldVariable.name,
      to: newVariable.name,
    })
  }
  if (oldVariable.type.name !== newVariable.type.name) {
    events.push({
      kind: 'type',
      from: oldVariable.type.name,
      to: newVariable.type.name,
    })
  }
  if (oldVariable.intervalUnit !== newVariable.intervalUnit) {
    events.push({
      kind: 'intervalUnit',
      from: oldVariable.intervalUnit,
      to: newVariable.intervalUnit,
    })
  }
  if (!_.isEqual(oldVariable.intervals, newVariable.intervals)) {
    events.push({
      kind: 'intervals',
      from: oldVariable.intervals,
      to: newVariable.intervals,
      fromStates: oldVariable.stateNames,
      toStates: newVariable.stateNames,
    })
  }
  // Continuous transition configs (KBE-758). The comparison is on the
  // STORED value with `null` (inherit the network default) intact —
  // resolving each side against its own network's default would report a
  // change on every inheriting semi-continuous variable whenever one
  // network's default band moved, which is O(N) noise for a one-line
  // edit. That default is reported once, at network level, instead.
  //
  // State names are the boundary identity here because this module pairs
  // arbitrary networks by copyReferenceId-or-name; a renamed state moves
  // its boundary, which the separate `states` change already reports.
  const oldTransitions = transitionSource(oldVariable)
  const newTransitions = transitionSource(newVariable)
  if (!haveSameStoredTransitions(oldTransitions, newTransitions)) {
    events.push({
      kind: 'transitionFunctions',
      from: storedTransitionsByBoundary(oldTransitions),
      to: storedTransitionsByBoundary(newTransitions),
    })
  }
  if (oldVariable.parameterizationType !== newVariable.parameterizationType) {
    events.push({
      kind: 'parameterizationType',
      from: oldVariable.parameterizationType,
      to: newVariable.parameterizationType,
    })
  }
  if (oldVariable.stateNames.join() !== newVariable.stateNames.join()) {
    events.push({
      kind: 'states',
      from: oldVariable.stateNames,
      to: newVariable.stateNames,
    })
  }
  if (
    (oldVariable.optimalStateName ?? '') !==
    (newVariable.optimalStateName ?? '')
  ) {
    events.push({
      kind: 'optimalState',
      from: oldVariable.optimalStateName ?? '',
      to: newVariable.optimalStateName ?? '',
    })
  }
  if (oldVariable.granularity !== newVariable.granularity) {
    events.push({
      kind: 'granularity',
      from: oldVariable.granularity,
      to: newVariable.granularity,
    })
  }
  if (oldVariable.comment !== newVariable.comment) {
    events.push({
      kind: 'comment',
      from: oldVariable.comment,
      to: newVariable.comment,
    })
  }
  if (!_.isEqual(oldVariable.tags ?? [], newVariable.tags ?? [])) {
    events.push({
      kind: 'tags',
      from: oldVariable.tags ?? [],
      to: newVariable.tags ?? [],
    })
  }
  if ((oldVariable.definition ?? '') !== (newVariable.definition ?? '')) {
    events.push({
      kind: 'definition',
      from: oldVariable.definition ?? '',
      to: newVariable.definition ?? '',
    })
  }
  if ((oldVariable.references ?? '') !== (newVariable.references ?? '')) {
    events.push({
      kind: 'references',
      from: oldVariable.references ?? '',
      to: newVariable.references ?? '',
    })
  }
  if ((oldVariable.formula ?? '') !== (newVariable.formula ?? '')) {
    events.push({
      kind: 'formula',
      from: oldVariable.formula ?? '',
      to: newVariable.formula ?? '',
    })
  }
  if (
    (oldVariable.prevalenceExplanation ?? '') !==
    (newVariable.prevalenceExplanation ?? '')
  ) {
    events.push({
      kind: 'prevalenceExplanation',
      from: oldVariable.prevalenceExplanation ?? '',
      to: newVariable.prevalenceExplanation ?? '',
    })
  }

  // Arrow comments: emit one event per inbound-arrow whose comment
  // text differs (including arrows present on only one side, where
  // missing-arrow is treated as `undefined` and an arrow with empty
  // comment is `''` — the text tool draws a meaningful word-level
  // diff against that distinction).
  const inboundNames = new Set([
    ...oldVariable.inboundArrows.map((a) => a.name),
    ...newVariable.inboundArrows.map((a) => a.name),
  ])
  for (const inboundName of inboundNames) {
    const oldArrow = oldVariable.inboundArrows.find(
      (a) => a.name === inboundName,
    )
    const newArrow = newVariable.inboundArrows.find(
      (a) => a.name === inboundName,
    )
    if (oldArrow?.comment !== newArrow?.comment) {
      events.push({
        kind: 'arrowComment',
        inboundVariableName: inboundName,
        from: oldArrow?.comment,
        to: newArrow?.comment,
      })
    }
  }

  return events
}
